import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "jobs-panel-list__divider"
}
const _hoisted_2 = {
  key: 0,
  class: "jobs-panel-list__loader-more"
}

import {
	computed,
	nextTick,
	onBeforeUnmount,
	ref,
	watch
} from "vue";
import { jobsStore } from "@/store/jobs";
import JobsPanelListItem from "@/components/main/jobs-panel/JobsPanelListItem.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";


export default /*@__PURE__*/_defineComponent({
  __name: 'JobsPanelList',
  setup(__props) {

const store = jobsStore()

const jobs = computed(() => store.allJobs)
const isLoadingMore = computed(() => store.isLoadingMore)

const bottomSentinelIndex = computed(() => {
	if (jobs.value.length === 0) return -1

	return Math.max(0, jobs.value.length - 5)
})

const topSentinelIndex = computed(() => {
	if (jobs.value.length === 0) return -1

	return Math.min(4, jobs.value.length - 1)
})

const $container = ref<HTMLElement | null>(null)
const isAutoScrolling = ref(false)

const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$container,
	'[data-job-id]',
	() => jobs.value
)

let bottomObserver: IntersectionObserver | null = null
let topObserver: IntersectionObserver | null = null
let currentBottomEl: Element | null = null
let currentTopEl: Element | null = null

const loadAbovePreservingScroll = async () => {
	if (!$container.value) return

	const prevHeight = $container.value.scrollHeight
	const prevTop = $container.value.scrollTop
	await store.loadMoreJobsAbove()
	await nextTick()
	$container.value.scrollTop = prevTop + ($container.value.scrollHeight - prevHeight)
}

const bindBottomSentinel = (el: Element | null) => {
	if (el === currentBottomEl) return

	if (bottomObserver) {
		bottomObserver.disconnect()
		bottomObserver = null
	}
	currentBottomEl = el
	if (el && $container.value) {
		bottomObserver = new IntersectionObserver((entries) => {
			if (isAutoScrolling.value) return
			if (entries[0]?.isIntersecting && store.hasMoreBelow) {
				store.loadMoreJobsBelow()
			}
		}, {
			root: $container.value,
			threshold: 0,
		})
		bottomObserver.observe(el)
	}
}

const bindTopSentinel = (el: Element | null) => {
	if (el === currentTopEl) return

	if (topObserver) {
		topObserver.disconnect()
		topObserver = null
	}
	currentTopEl = el
	if (el && $container.value) {
		topObserver = new IntersectionObserver((entries) => {
			if (isAutoScrolling.value) return
			if (entries[0]?.isIntersecting && store.hasMoreAbove) {
				loadAbovePreservingScroll()
			}
		}, {
			root: $container.value,
			threshold: 0,
		})
		topObserver.observe(el)
	}
}

watch(() => store.jobsLoading, (newVal) => {
	if (newVal && !store.highlightedJobId) {
		$container.value?.scrollTo({ top: 0 })
	}
})

watch(() => store.highlightedJobId, async (id) => {
	if (!id) return

	await nextTick()
	const el = $container.value?.querySelector<HTMLElement>(`[data-job-id="${id}"]`)
	if (!el) return

	isAutoScrolling.value = true
	el.scrollIntoView({ block: 'center', behavior: 'smooth' })
	setTimeout(() => { isAutoScrolling.value = false }, 700)

	el.classList.add('jobs-panel-list-item--highlight')
	const onEnd = () => {
		el.classList.remove('jobs-panel-list-item--highlight')
		el.removeEventListener('animationend', onEnd)
		store.clearHighlightedJob()
	}
	el.addEventListener('animationend', onEnd)
}, { immediate: true })

onBeforeUnmount(() => {
	bottomObserver?.disconnect()
	topObserver?.disconnect()
	bottomObserver = null
	topObserver = null
	currentBottomEl = null
	currentTopEl = null
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "$container",
    ref: $container,
    class: "jobs-panel-list"
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(jobs.value, (job, index) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: job.Id
      }, [
        (index === topSentinelIndex.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_for: true,
              ref: (el) => bindTopSentinel(el),
              class: "jobs-panel-list__sentinel",
              "aria-hidden": "true"
            }, null, 512))
          : _createCommentVNode("", true),
        _createVNode(JobsPanelListItem, { job: job }, null, 8, ["job"]),
        (index + 1 < jobs.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1))
          : _createCommentVNode("", true),
        (index === bottomSentinelIndex.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 2,
              ref_for: true,
              ref: (el) => bindBottomSentinel(el),
              class: "jobs-panel-list__sentinel",
              "aria-hidden": "true"
            }, null, 512))
          : _createCommentVNode("", true)
      ], 64))
    }), 128)),
    (isLoadingMore.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(AppLoader)
        ]))
      : _createCommentVNode("", true),
    _createVNode(AppScrollTopButton, {
      enabled: _unref(isScrollTopEnabled),
      onOnClick: _unref(scrollToTop)
    }, null, 8, ["enabled", "onOnClick"])
  ], 512))
}
}

})